// vg-binding: P7.D-44
// vg-binding: P7.D-46
// vg-binding: P7.D-50
//
// apps/vendor/src/features/wallet/api.ts
//
// TanStack-Query surface wrapping the Wave 10 BE wallet endpoints.

import { csrfFetch } from '../../lib/api/csrfFetch';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

interface ApiOk<T> { ok: true; data: T }
interface ApiErr {
  ok: false;
  error: {
    code: string;
    message: string;
    user_message?: string;
    retry_after_ms?: number;
  };
}

export class WalletApiError extends Error {
  public readonly code: string;
  public readonly statusCode: number;
  public readonly userMessage: string | undefined;
  public readonly retryAfterMs: number | undefined;
  constructor(
    code: string,
    message: string,
    statusCode: number,
    extras?: { user_message?: string; retry_after_ms?: number },
  ) {
    super(message);
    this.name = 'WalletApiError';
    this.code = code;
    this.statusCode = statusCode;
    this.userMessage = extras?.user_message;
    this.retryAfterMs = extras?.retry_after_ms;
  }
}

async function unwrap<T>(res: Response): Promise<T> {
  const body = (await res.json().catch(() => null)) as ApiOk<T> | ApiErr | null;
  if (!body) {
    throw new WalletApiError('NETWORK', 'Empty body', res.status);
  }
  if (!body.ok) {
    throw new WalletApiError(body.error.code, body.error.message, res.status, {
      ...(body.error.user_message !== undefined ? { user_message: body.error.user_message } : {}),
      ...(body.error.retry_after_ms !== undefined ? { retry_after_ms: body.error.retry_after_ms } : {}),
    });
  }
  return body.data;
}

// ---------------------------------------------------------------------------
// Summary
// ---------------------------------------------------------------------------

export interface WalletSummary {
  available_cents: number;
  pending_cents: number;
  /** R3-4 (2026-05-16): amount held by in-flight withdrawal requests. */
  pending_withdraw_cents?: number;
  paid_out_cents: number;
  lifetime_total_cents: number;
  currency: string;
}

const WALLET_SUMMARY_KEY = ['vendor', 'wallet', 'summary'] as const;
const WALLET_TX_KEY = ['vendor', 'wallet', 'transactions'] as const;
const WALLET_WD_KEY = ['vendor', 'wallet', 'withdrawals'] as const;
const WALLET_BANKS_KEY = ['vendor', 'wallet', 'banks'] as const;

export function useWalletSummary(): UseQueryResult<WalletSummary, WalletApiError> {
  return useQuery<WalletSummary, WalletApiError>({
    queryKey: WALLET_SUMMARY_KEY,
    queryFn: async () => {
      const res = await csrfFetch('/api/v1/vendor/wallet/summary', {
        credentials: 'include',
      });
      return unwrap<WalletSummary>(res);
    },
    refetchInterval: 60 * 1000,
  });
}

// ---------------------------------------------------------------------------
// Transactions
// ---------------------------------------------------------------------------

export interface TransactionRow {
  ledger_id: string;
  type: string;
  amount_cents: number;
  currency: string;
  status: string;
  order_id: string | null;
  order_item_id: string | null;
  shipped_at: number | null;
  available_at: number | null;
  breakdown: unknown;
  created_at: number | null;
}

export interface TransactionsListData {
  rows: TransactionRow[];
  next_cursor: string | null;
  total: number;
  page: number;
  page_count: number;
  limit: number;
}

export function useTransactions(params?: {
  type?: string;
  page?: number;
  limit?: number;
}): UseQueryResult<TransactionsListData, WalletApiError> {
  return useQuery({
    queryKey: [...WALLET_TX_KEY, params],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params?.type) qs.set('type', params.type);
      if (params?.page) qs.set('page', String(params.page));
      if (params?.limit) qs.set('limit', String(params.limit));
      const qstr = qs.toString();
      const res = await csrfFetch(
        `/api/v1/vendor/wallet/transactions${qstr ? `?${qstr}` : ''}`,
        { credentials: 'include' },
      );
      return unwrap<TransactionsListData>(res);
    },
  });
}

// ---------------------------------------------------------------------------
// Withdraw + history
// ---------------------------------------------------------------------------

export interface WithdrawalRow {
  withdrawal_id: string;
  amount_cents: number;
  currency: string;
  status: string;
  requested_at: number;
  reviewed_at: number | null;
  is_flagged_suspicious: boolean;
  flag_reasons: string[];
}

export interface WithdrawalsListData {
  rows: WithdrawalRow[];
  next_cursor: string | null;
  total: number;
  page: number;
  page_count: number;
  limit: number;
}

export function useWithdrawalsHistory(params?: {
  page?: number;
  limit?: number;
}): UseQueryResult<WithdrawalsListData, WalletApiError> {
  return useQuery({
    queryKey: [...WALLET_WD_KEY, params],
    queryFn: async () => {
      const qs = new URLSearchParams();
      if (params?.page) qs.set('page', String(params.page));
      if (params?.limit) qs.set('limit', String(params.limit));
      const qstr = qs.toString();
      const res = await csrfFetch(
        `/api/v1/vendor/wallet/withdrawals${qstr ? `?${qstr}` : ''}`,
        { credentials: 'include' },
      );
      return unwrap<WithdrawalsListData>(res);
    },
  });
}

export interface InitiateWithdrawInput {
  amount_cents: number;
  payment_method_id: string;
  /** UAT #2185 — 6-digit OTP mailed via useSendWithdrawOtp. */
  otp_code: string;
}

/** UAT #2185 — dispatch the withdraw-confirmation OTP email. */
export function useSendWithdrawOtp(): UseMutationResult<
  { sent: boolean; channel: string; expires_in_seconds: number },
  WalletApiError,
  void
> {
  return useMutation({
    mutationFn: async () => {
      const res = await csrfFetch('/api/v1/vendor/wallet/withdraw/otp', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      return unwrap<{ sent: boolean; channel: string; expires_in_seconds: number }>(res);
    },
  });
}

export function useInitiateWithdraw(): UseMutationResult<
  {
    withdrawal_id: string;
    status: string;
    is_flagged_suspicious: boolean;
    flag_reasons: string[];
    requires_otp: boolean;
  },
  WalletApiError,
  InitiateWithdrawInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      const res = await csrfFetch('/api/v1/vendor/wallet/withdraw/initiate', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      return unwrap<{
        withdrawal_id: string;
        status: string;
        is_flagged_suspicious: boolean;
        flag_reasons: string[];
        requires_otp: boolean;
      }>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: WALLET_SUMMARY_KEY });
      void qc.invalidateQueries({ queryKey: WALLET_WD_KEY });
    },
  });
}

export function useCancelWithdraw(): UseMutationResult<{ status: 'cancelled' }, WalletApiError, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (withdrawal_id) => {
      const res = await csrfFetch(
        `/api/v1/vendor/wallet/withdrawals/${withdrawal_id}/cancel`,
        { method: 'PATCH', credentials: 'include' },
      );
      return unwrap<{ status: 'cancelled' }>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: WALLET_WD_KEY });
      void qc.invalidateQueries({ queryKey: WALLET_SUMMARY_KEY });
    },
  });
}

// ---------------------------------------------------------------------------
// Bank accounts
// ---------------------------------------------------------------------------

export interface BankRow {
  bank_id: string;
  method: 'bank' | 'payoneer' | 'wise';
  account_holder: string;
  account_number: string;
  bank_name: string | null;
  swift_code: string | null;
  is_default: boolean;
  is_verified: boolean;
  verified_at: number | null;
}

export function useBankAccounts(): UseQueryResult<{ rows: BankRow[] }, WalletApiError> {
  return useQuery({
    queryKey: WALLET_BANKS_KEY,
    queryFn: async () => {
      const res = await csrfFetch('/api/v1/vendor/wallet/bank-accounts', {
        credentials: 'include',
      });
      return unwrap<{ rows: BankRow[] }>(res);
    },
  });
}

export interface CreateBankInput {
  method: 'bank' | 'payoneer' | 'wise';
  account_holder: string;
  account_number: string;
  bank_name?: string;
  swift_code?: string;
  is_default?: boolean;
}

export function useCreateBank(): UseMutationResult<{ bank_id: string }, WalletApiError, CreateBankInput> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input) => {
      const res = await csrfFetch('/api/v1/vendor/wallet/bank-accounts', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      });
      return unwrap<{ bank_id: string }>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: WALLET_BANKS_KEY });
    },
  });
}

export function useDeleteBank(): UseMutationResult<void, WalletApiError, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (bank_id) => {
      const res = await csrfFetch(`/api/v1/vendor/wallet/bank-accounts/${bank_id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      await unwrap<{ deleted: true }>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: WALLET_BANKS_KEY });
    },
  });
}

export function useSetDefaultBank(): UseMutationResult<void, WalletApiError, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (bank_id) => {
      const res = await csrfFetch(
        `/api/v1/vendor/wallet/bank-accounts/${bank_id}/set-default`,
        { method: 'PATCH', credentials: 'include' },
      );
      await unwrap<{ default_set: true }>(res);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: WALLET_BANKS_KEY });
    },
  });
}
